import { useRef, useState } from 'react';
import type { JSX } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { dateTime } from '../lib/format.js';
import { Dialog, EmptyState, ErrorState, LoadingState } from './ui.js';

/**
 * Galerie obrázků pro e-maily: nahrání nového souboru, výběr z nahraných a z těch, které už šablony používají.
 * Vybraná adresa se vloží do pole, ze kterého se galerie otevřela.
 */

interface Gallery {
  uploaded: Array<{
    id: string;
    filename: string;
    bytes: number;
    width: number | null;
    height: number | null;
    uploadedAt: string;
    url: string;
  }>;
  used: Array<{ url: string; templates: number }>;
}

const MAX_WIDTH = 1200;

/** Zmenší velký obrázek na šířku 1200 px (e-mail nepotřebuje víc) a vrátí base64 bez předpony. */
async function prepare(
  file: File,
): Promise<{ dataBase64: string; contentType: string; width: number; height: number }> {
  const dataUrl = await new Promise<string>((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = () => reject(new Error('Soubor se nepodařilo přečíst.'));
    r.readAsDataURL(file);
  });
  const img = await new Promise<HTMLImageElement>((resolve, reject) => {
    const i = new Image();
    i.onload = () => resolve(i);
    i.onerror = () => reject(new Error('Soubor není obrázek.'));
    i.src = dataUrl;
  });
  const keep = file.type === 'image/gif' || (img.width <= MAX_WIDTH && file.size <= 1_500_000);
  if (keep) {
    return {
      dataBase64: dataUrl.split(',')[1] ?? '',
      contentType: file.type,
      width: Math.min(img.width, 2000),
      height: Math.min(img.height, 2000),
    };
  }
  const scale = Math.min(1, MAX_WIDTH / img.width);
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(img.width * scale);
  canvas.height = Math.round(img.height * scale);
  canvas.getContext('2d')?.drawImage(img, 0, 0, canvas.width, canvas.height);
  const out = canvas.toDataURL('image/jpeg', 0.85);
  return {
    dataBase64: out.split(',')[1] ?? '',
    contentType: 'image/jpeg',
    width: canvas.width,
    height: canvas.height,
  };
}

export function ImagePickButton({
  onPick,
  disabled,
  label = 'Galerie',
}: {
  onPick: (url: string) => void;
  disabled?: boolean;
  label?: string;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        className="btn"
        disabled={disabled}
        onClick={() => setOpen(true)}
        title="Vybrat nebo nahrát obrázek"
      >
        {label}
      </button>
      {open ? (
        <ImageGallery
          onClose={() => setOpen(false)}
          onPick={(url) => {
            onPick(url);
            setOpen(false);
          }}
        />
      ) : null}
    </>
  );
}

function ImageGallery({
  onPick,
  onClose,
}: {
  onPick: (url: string) => void;
  onClose: () => void;
}): JSX.Element {
  const client = useQueryClient();
  const [tab, setTab] = useState<'uploaded' | 'used'>('uploaded');
  const [error, setError] = useState<string | null>(null);
  const file = useRef<HTMLInputElement>(null);
  const q = useQuery({
    queryKey: ['template-assets'],
    queryFn: () => api.get<Gallery>('/api/template-assets'),
  });
  const upload = useMutation({
    mutationFn: async (f: File) => {
      if (!/^image\/(png|jpeg|gif|webp)$/.test(f.type))
        throw new Error('Nahrát jde PNG, JPG, GIF nebo WebP.');
      const p = await prepare(f);
      return api.post<{ url: string; warning: string | null }>('/api/template-assets', {
        filename: f.name,
        ...p,
      });
    },
    onSuccess: () => {
      setError(null);
      void client.invalidateQueries({ queryKey: ['template-assets'] });
    },
    onError: (e: Error) => setError(e.message),
  });

  return (
    <Dialog open wide title="Galerie obrázků" onClose={onClose}>
      <div className="stack">
        <div className="row">
          <input
            ref={file}
            type="file"
            accept="image/png,image/jpeg,image/gif,image/webp"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) upload.mutate(f);
              e.target.value = '';
            }}
            aria-label="Nahrát obrázek"
          />
          {upload.isPending ? <span className="note">Nahrávám…</span> : null}
        </div>
        <p className="note">
          Velké obrázky se zmenší na šířku 1200 px. Nahrát jde PNG, JPG, GIF a WebP do 2 MB.
        </p>
        {error ? <ErrorState title="Nahrání se nepovedlo" error={new Error(error)} /> : null}
        <div className="seg" role="tablist" aria-label="Zdroj obrázků">
          <button
            role="tab"
            aria-selected={tab === 'uploaded'}
            className={tab === 'uploaded' ? 'on' : ''}
            onClick={() => setTab('uploaded')}
          >
            Nahrané ({q.data?.uploaded.length ?? 0})
          </button>
          <button
            role="tab"
            aria-selected={tab === 'used'}
            className={tab === 'used' ? 'on' : ''}
            onClick={() => setTab('used')}
          >
            Už použité v šablonách ({q.data?.used.length ?? 0})
          </button>
        </div>
        {q.isLoading ? <LoadingState what="obrázky" /> : null}
        {q.isError ? <ErrorState title="Galerii se nepodařilo načíst" error={q.error} /> : null}
        {q.data && tab === 'uploaded' ? (
          q.data.uploaded.length === 0 ? (
            <EmptyState title="Zatím tu nic není">
              Nahrajte první obrázek tlačítkem výše.
            </EmptyState>
          ) : (
            <div className="gallery-grid">
              {q.data.uploaded.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  className="gallery-item"
                  onClick={() => onPick(a.url)}
                  title={`${a.filename} · ${a.width ?? '?'}×${a.height ?? '?'} · ${dateTime(a.uploadedAt)}`}
                >
                  <img src={a.url} alt={a.filename} loading="lazy" />
                  <small>{a.filename}</small>
                </button>
              ))}
            </div>
          )
        ) : null}
        {q.data && tab === 'used' ? (
          q.data.used.length === 0 ? (
            <EmptyState title="Šablony zatím žádné obrázky nepoužívají">
              Použité adresy se ukážou tady.
            </EmptyState>
          ) : (
            <div className="gallery-grid">
              {q.data.used.map((u) => (
                <button
                  key={u.url}
                  type="button"
                  className="gallery-item"
                  onClick={() => onPick(u.url)}
                  title={u.url}
                >
                  <img src={u.url} alt="" loading="lazy" />
                  <small>použito v {u.templates} šablonách</small>
                </button>
              ))}
            </div>
          )
        ) : null}
      </div>
    </Dialog>
  );
}
